import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, getApiToken, getGraphToken } from '../services/authConfig';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// ===== ROLE DEFINITIONS =====
export const ROLES = {
  ADMIN: 'admin',
  EDITOR: 'editor',
  VIEWER: 'viewer',
};

export const ROLE_LABELS = {
  admin: 'מנהל מערכת',
  editor: 'עריכה',
  viewer: 'צפייה בלבד',
};

export const ROLE_DESCRIPTIONS = {
  admin: 'שליטה מלאה על כל המערכת - ניהול משתמשים, הגדרות, מחיקה',
  editor: 'יצירה, עריכה, העברה והעתקה של מכרזים',
  viewer: 'צפייה בלבד בכל המכרזים והנתונים',
};

// ===== PERMISSIONS MATRIX =====
const PERMISSIONS = {
  'tender:create': ['admin', 'editor'],
  'tender:edit': ['admin', 'editor'],
  'tender:delete': ['admin'],
  'tender:move': ['admin', 'editor'],
  'tender:copy': ['admin', 'editor'],
  'tender:color': ['admin', 'editor'],
  'data:import': ['admin', 'editor'],
  'data:export': ['admin', 'editor', 'viewer'],
  'notes:add': ['admin', 'editor', 'viewer'],
  'users:manage': ['admin'],
  'settings:manage': ['admin'],
  'system:audit': ['admin'],
};

const API_BASE = process.env.REACT_APP_API_URL || '';

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Fetch user profile from our backend
  const fetchUserProfile = useCallback(async () => {
    try {
      const token = await getApiToken();
      const response = await fetch(`${API_BASE}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 403) {
        setAuthError('אין לך הרשאה לגשת למערכת. פנה למנהל המערכת.');
        setCurrentUser(null);
        setLoading(false);
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch profile');

      const data = await response.json();

      setCurrentUser({
        id: accounts[0]?.localAccountId || '',
        name: data.displayName || accounts[0]?.name || '',
        email: data.email || accounts[0]?.username || '',
        role: data.role || 'viewer',
        photo: data.photo || null,
      });
      setAuthError(null);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // If API fails, use basic info from token with viewer role
      if (accounts.length > 0) {
        setCurrentUser({
          id: accounts[0].localAccountId,
          name: accounts[0].name || '',
          email: accounts[0].username || '',
          role: 'viewer',
          photo: null,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [accounts, API_BASE]);

  // Fetch user photo from Graph API
  const fetchUserPhoto = useCallback(async () => {
    try {
      const token = await getGraphToken();
      const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const photoUrl = URL.createObjectURL(blob);
        setCurrentUser(prev => prev ? { ...prev, photo: photoUrl } : prev);
      }
    } catch {
      // No photo available
    }
  }, []);

  // Login effect
  useEffect(() => {
    if (isAuthenticated && accounts.length > 0 && inProgress === InteractionStatus.None) {
      fetchUserProfile().then(() => fetchUserPhoto());
    } else if (!isAuthenticated && inProgress === InteractionStatus.None) {
      setLoading(false);
    }
  }, [isAuthenticated, accounts, inProgress, fetchUserProfile, fetchUserPhoto]);

  // Login
  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, [instance]);

  // Logout
  const logout = useCallback(async () => {
    await instance.logoutRedirect();
  }, [instance]);

  // Permission checks
  const hasPermission = useCallback((permission) => {
    if (!currentUser) return false;
    const allowedRoles = PERMISSIONS[permission];
    if (!allowedRoles) return false;
    return allowedRoles.includes(currentUser.role);
  }, [currentUser]);

  const hasRole = useCallback((requiredRole) => {
    if (!currentUser) return false;
    const hierarchy = { admin: 3, editor: 2, viewer: 1 };
    return (hierarchy[currentUser.role] || 0) >= (hierarchy[requiredRole] || 0);
  }, [currentUser]);

  const canCreate = currentUser ? hasPermission('tender:create') : false;
  const canEdit = currentUser ? hasPermission('tender:edit') : false;
  const canDelete = currentUser ? hasPermission('tender:delete') : false;
  const canMove = currentUser ? hasPermission('tender:move') : false;
  const canImport = currentUser ? hasPermission('data:import') : false;

  const value = {
    currentUser,
    setCurrentUser,
    isAuthenticated,
    loading,
    authError,
    login,
    logout,
    hasPermission,
    hasRole,
    canCreate,
    canEdit,
    canDelete,
    canMove,
    canImport,
    ROLES,
    ROLE_LABELS,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
